<template>
  <div class="space-y-4">
    <div class="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
      <div class="flex flex-wrap items-center gap-2">
        <button type="button" class="table-action-button is-neutral" title="前一天" aria-label="前一天" @click="emitDateOffset(-1)">
          <ChevronLeftIcon class="h-4 w-4" />
        </button>
        <label class="min-w-0">
          <span class="sr-only">日报日期</span>
          <input :value="reportDate" type="date" class="form-input w-full sm:w-auto" @change="emit('update:reportDate', $event.target.value)">
        </label>
        <button type="button" class="table-action-button is-neutral" title="后一天" aria-label="后一天" @click="emitDateOffset(1)">
          <ChevronRightIcon class="h-4 w-4" />
        </button>
      </div>

      <div class="flex flex-wrap items-center gap-2 text-sm">
        <span class="text-slate-500">计划</span>
        <ReportStatusBadge :status="phaseDisplayStatus('plan')" />
        <span class="text-slate-500">总结</span>
        <ReportStatusBadge :status="phaseDisplayStatus('summary')" />
      </div>
    </div>

    <div class="grid gap-4 border-t border-slate-100 pt-4 text-sm sm:grid-cols-2 xl:grid-cols-3">
      <div v-if="activeDeadlineInfo" class="flex items-center gap-3 rounded-lg px-3 py-2.5" :class="activeDeadlineInfo.bgClass">
        <div class="flex h-8 w-8 shrink-0 items-center justify-center rounded-md" :class="activeDeadlineInfo.iconBgClass">
          <component :is="activeDeadlineInfo.icon" class="h-4 w-4" :class="activeDeadlineInfo.iconClass" />
        </div>
        <div class="min-w-0">
          <div class="text-xs" :class="activeDeadlineInfo.labelClass">{{ activeDeadlineInfo.label }}</div>
          <div class="truncate text-sm font-medium" :class="activeDeadlineInfo.valueClass">{{ activeDeadlineInfo.text }}</div>
          <div v-if="activeDeadlineInfo.subtext" class="mt-0.5 truncate text-xs" :class="activeDeadlineInfo.subtextClass">{{ activeDeadlineInfo.subtext }}</div>
        </div>
      </div>
      <div class="flex items-center gap-3 rounded-lg bg-slate-50 px-3 py-2.5">
        <div class="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-violet-50">
          <UserIcon class="h-4 w-4 text-violet-500" />
        </div>
        <div class="min-w-0">
          <div class="text-xs text-slate-400">直属主管</div>
          <div class="truncate text-sm font-medium text-slate-700">{{ supervisor }}</div>
        </div>
      </div>
      <div class="flex items-center gap-3 rounded-lg px-3 py-2.5" :class="saveStatusBg">
        <div class="flex h-8 w-8 shrink-0 items-center justify-center rounded-md" :class="saveStatusIconBg">
          <component :is="saveStatusIcon" class="h-4 w-4" :class="saveStatusIconColor" />
        </div>
        <div class="min-w-0">
          <div class="text-xs text-slate-400">保存状态</div>
          <div class="truncate text-sm font-medium" :class="saveStatusClass">{{ saveStatusLabel }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { CalendarIcon, CheckCircleIcon, ChevronLeftIcon, ChevronRightIcon, ClockIcon, ExclamationCircleIcon, UserIcon } from '@heroicons/vue/24/outline'
import { formatReportDisplayTime, supervisorLabel } from '../workReportUtils'
import ReportStatusBadge from './ReportStatusBadge.vue'

const props = defineProps({
  report: { type: Object, default: () => ({}) },
  reportDate: { type: String, required: true },
  activePhase: { type: String, default: 'plan' },
  saveStatus: { type: String, default: 'idle' }
})
const emit = defineEmits(['update:reportDate'])

const INELIGIBLE_STATUSES = new Set(['AUTO_EXEMPT', 'MANUAL_EXEMPT', 'PENDING_STATUS_CONFIRMATION'])

const supervisor = computed(() => supervisorLabel(props.report))
const activeDeadlineInfo = computed(() => {
  if (!props.report || INELIGIBLE_STATUSES.has(props.report.eligibilityStatus)) return null

  const phase = props.activePhase === 'summary' ? 'summary' : 'plan'
  const meta = deadlinePhaseMeta(phase)
  const value = deadline(meta)
  if (!value || value === '--') return null

  const missing = Boolean(props.report?.[meta.missingField])
  const late = Boolean(props.report?.[meta.lateField])
  const submitted = props.report?.[meta.statusField] === 'SUBMITTED'
  const submittedAt = deadlineSubmittedAt(props.report?.[meta.submittedAtField])
  const visual = deadlineVisualState(missing ? 'missing' : late ? 'late' : submitted ? 'submitted' : 'normal')
  const prefix = missing ? '缺交' : late ? '迟交' : submitted ? '已提交' : '未提交'

  return {
    ...visual,
    icon: missing || late ? ExclamationCircleIcon : meta.icon,
    label: meta.label,
    text: `${prefix} · 截止 ${formatDeadlineValue(value)}`,
    subtext: submittedAt
  }
})
const saveStatusMeta = computed(() => ({
  saving: ['正在保存', 'text-blue-600', ClockIcon, 'bg-blue-50', 'text-blue-500', 'bg-blue-50'],
  saved: ['已保存', 'text-emerald-600', CheckCircleIcon, 'bg-emerald-50', 'text-emerald-500', 'bg-emerald-50/70'],
  error: ['保存失败，点击重试', 'text-rose-600', ExclamationCircleIcon, 'bg-rose-50', 'text-rose-500', 'bg-rose-50/70'],
  conflict: ['保存冲突，请重新加载', 'text-rose-600', ExclamationCircleIcon, 'bg-rose-50', 'text-rose-500', 'bg-rose-50/70'],
  dirty: ['存在未保存修改', 'text-amber-600', ExclamationCircleIcon, 'bg-amber-50', 'text-amber-500', 'bg-amber-50/70'],
  idle: ['等待编辑', 'text-slate-600', ClockIcon, 'bg-slate-100', 'text-slate-400', 'bg-slate-50']
}[props.saveStatus] || ['等待编辑', 'text-slate-600', ClockIcon, 'bg-slate-100', 'text-slate-400', 'bg-slate-50']))
const saveStatusLabel = computed(() => saveStatusMeta.value[0])
const saveStatusClass = computed(() => saveStatusMeta.value[1])
const saveStatusIcon = computed(() => saveStatusMeta.value[2])
const saveStatusIconBg = computed(() => saveStatusMeta.value[3])
const saveStatusIconColor = computed(() => saveStatusMeta.value[4])
const saveStatusBg = computed(() => saveStatusMeta.value[5])

function phaseDisplayStatus(phase) {
  const missingField = phase === 'plan' ? 'planMissing' : 'summaryMissing'
  const lateField = phase === 'plan' ? 'planLate' : 'summaryLate'
  if (props.report?.[missingField]) return 'MISSING'
  if (props.report?.[lateField]) return 'LATE'
  return props.report?.[`${phase}Status`] || 'DRAFT'
}

function deadline(meta) {
  return props.report?.[meta.deadlineField] || props.report?.deadlines?.[meta.phase] || '--'
}

function deadlinePhaseMeta(phase) {
  if (phase === 'summary') {
    return {
      phase: 'summary',
      label: '总结截止',
      icon: ClockIcon,
      deadlineField: 'summaryDeadline',
      statusField: 'summaryStatus',
      missingField: 'summaryMissing',
      lateField: 'summaryLate',
      submittedAtField: 'summarySubmittedAt'
    }
  }

  return {
    phase: 'plan',
    label: '计划截止',
    icon: CalendarIcon,
    deadlineField: 'planDeadline',
    statusField: 'planStatus',
    missingField: 'planMissing',
    lateField: 'planLate',
    submittedAtField: 'planSubmittedAt'
  }
}

function deadlineVisualState(state) {
  const map = {
    missing: ['bg-rose-50/80', 'bg-rose-100', 'text-rose-500', 'text-rose-500', 'text-rose-700', 'text-rose-500'],
    late: ['bg-amber-50/80', 'bg-amber-100', 'text-amber-500', 'text-amber-500', 'text-amber-700', 'text-amber-500'],
    submitted: ['bg-slate-50', 'bg-emerald-50', 'text-emerald-500', 'text-slate-400', 'text-slate-700', 'text-slate-400'],
    normal: ['bg-slate-50', 'bg-blue-50', 'text-blue-500', 'text-slate-400', 'text-slate-700', 'text-slate-400']
  }
  const [bgClass, iconBgClass, iconClass, labelClass, valueClass, subtextClass] = map[state] || map.normal
  return { bgClass, iconBgClass, iconClass, labelClass, valueClass, subtextClass }
}

function deadlineSubmittedAt(value) {
  if (!value) return ''
  const formatted = formatReportDisplayTime(value)
  return formatted && formatted !== '--' ? `提交 ${formatted}` : ''
}

function formatDeadlineValue(value) {
  if (!value) return '--'
  const text = String(value).trim()
  if (!text) return '--'

  const dateTimeMatch = text.replace(' ', 'T').match(/T(\d{2}:\d{2})/)
  if (dateTimeMatch) return dateTimeMatch[1]

  const timeMatch = text.match(/^(\d{2}:\d{2})(?::\d{2})?$/)
  if (timeMatch) return timeMatch[1]

  return text
}

function emitDateOffset(offset) {
  const value = new Date(`${props.reportDate}T00:00:00`)
  value.setDate(value.getDate() + offset)
  emit('update:reportDate', formatLocalDate(value))
}

function formatLocalDate(date) {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}
</script>
